import React, { useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    Typography,
    Stepper,
    Step,
    StepLabel,
    Button,
    Paper,
    Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { IMPORT_DATA_TYPE_TEMPLATES } from '../config/importDataTypeTemplates';
import { importCustomDataThunk } from '../store/thunks/mdsThunks';
import { useToast } from "../components/common/custom-component/ToastProvider";
// Lazy-load step components so the file stays manageable
import ImportStepUpload from '../components/common/ImportDataTypes/steps/ImportStepUpload';
import ImportStepPreview from '../components/common/ImportDataTypes/steps/ImportStepPreview';
import ImportStepConfirm from '../components/common/ImportDataTypes/steps/ImportStepConfirm';

const STEPS = ['Upload File', 'Preview', 'Confirm'];

export default function ImportWizardPage() {
    const navigate = useNavigate();
    const { state } = useLocation();
    const dispatch = useDispatch();
    const [t] = useTranslation();
    const { success, error: ToastError, warning, info, } = useToast();

    const [activeStep, setActiveStep] = useState(0);
    const [parsedRows, setParsedRows] = useState([]);
    const [parsedHeaders, setParsedHeaders] = useState([]);
    const [uploadError, setUploadError] = useState(null);
    const [importing, setImporting] = useState(false);
    const [importError, setImportError] = useState(null);

    const typeConfig = state?.typeConfig;
    const importTypeKey = typeConfig?.key;

    const selectedCompany = useSelector((state) => state.selectedCompany.data?.companyData);

    const COMPANY_SCOPED_KEYS = ['tbl_users', 'tbl_generator', 'tbl_generator_contract'];
    const needsCompany = COMPANY_SCOPED_KEYS.includes(importTypeKey);

    const handleNext = () => setActiveStep((s) => s + 1);
    const handleBack = () => setActiveStep((s) => s - 1);
    const handleReset = () => {
        setActiveStep(0);
        setParsedRows([]);
        setParsedHeaders([]);
        setUploadError(null);
        setImportError(null);
    };

    const handleImport = async () => {
        setImporting(true);
        setImportError(null);

        // const result = await dispatch(importDataThunk({
        //     table_key: importTypeKey,
        //     body: {table_row:parsedRows,table_headers:parsedHeaders},
        //     ...(needsCompany && {
        //         companyId: selectedCompany?.companyId,
        //         keycloakCompanyId: selectedCompany?.keycloakCompanyId,
        //     }),
        // }));

        const result = await dispatch(importCustomDataThunk({
            tableKey: importTypeKey,
            bodyData: { table_row: parsedRows, table_headers: parsedHeaders }
        }));


        setImporting(false);

        if (importCustomDataThunk.fulfilled.match(result)) {
            navigate('/import-data');
        } else if (importCustomDataThunk.rejected.match(result)) {
            setImportError(result.payload || t('Import failed. Please try again.'));
            ToastError(result.payload)
        }
    };

    const stepContent = () => {
        switch (activeStep) {
            case 0:
                return (
                    <ImportStepUpload
                        typeConfig={typeConfig}
                        onParsed={(headers, rows) => {
                            setParsedHeaders(headers);
                            setParsedRows(rows);
                            setUploadError(null);
                        }}
                        onError={setUploadError}
                        onNext={handleNext}
                        uploadError={uploadError}
                    />
                );
            case 1:
                return (
                    <ImportStepPreview
                        headers={parsedHeaders}
                        rows={parsedRows}
                        onBack={handleBack}
                        onNext={handleNext}
                    />
                );
            case 2:
                return (
                    <ImportStepConfirm
                        typeConfig={typeConfig}
                        headers={parsedHeaders}
                        rows={parsedRows}
                        onBack={handleBack}
                        onReset={handleReset}
                        onDone={handleImport}
                        loading={importing}
                        error={importError}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
            {/* Back link */}
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/import-data')}
                sx={{ mb: 2, textTransform: 'none' }}
                color="inherit"
            >
                {t('Import Data')}
            </Button>

            {/* Title */}
            <Typography variant="h5" fontWeight="bold" gutterBottom>
                {t('Import')} {t(typeConfig.label)}
            </Typography>

            {/* Stepper */}
            <Stepper activeStep={activeStep} sx={{ my: 3 }}>
                {STEPS.map((label) => (
                    <Step key={label}>
                        <StepLabel>{t(label)}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            {/* Step content */}
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                {stepContent()}
            </Paper>
        </Box>
    );
}
